import type { LoginResponse } from "~/types/auth";

export interface SchoolLogoCrop {
  x: number;
  y: number;
  width: number;
  height: number;
}

export type SchoolLoginLayout = "split" | "centered";

// Optional while older saved sessions/API responses are still in use.
export interface SchoolLogoLayout {
  logo_crop?: SchoolLogoCrop;
  logo_scale_percent?: number;
  logo_offset_x?: number;
  logo_offset_y?: number;
  header_padding_removed?: boolean;
}

export interface OrganizationPreferencesResponse extends SchoolLogoLayout {
  minimum_students_to_start_class?: number | null;
  content_policy: "platform_and_school" | "school_only";
  logo_url: string | null;
  favicon_url?: string | null;
  login_hero_image_url?: string | null;
  login_hero_crop?: SchoolLogoCrop;
  login_layout?: SchoolLoginLayout;
  login_show_school_name?: boolean;
  primary_color: string;
  secondary_color: string;
  white_label_enabled: boolean;
  custom_domain: string | null;
  domain_verification_token?: string;
  domain_verified: boolean;
  domain_activation_status?: "inactive" | "active" | "legacy_pending";
  domain_activated_at?: string | null;
}

export type OrganizationContentPolicy =
  OrganizationPreferencesResponse["content_policy"];

export interface OrganizationBrandResponse extends SchoolLogoLayout {
  organization_id: number;
  slug: string;
  school_name: string;
  logo_url: string | null;
  favicon_url?: string | null;
  primary_color: string;
  secondary_color: string;
  white_label_enabled: boolean;
}

export interface OrganizationPreferencesUpdateInput extends SchoolLogoLayout {
  minimum_students_to_start_class?: number | null;
  content_policy?: OrganizationContentPolicy;
  logo?: File | null;
  favicon?: File | null;
  login_hero_image?: File | null;
  login_hero_crop?: SchoolLogoCrop;
  login_layout?: SchoolLoginLayout;
  login_show_school_name?: boolean;
  primary_color?: string;
  secondary_color?: string;
  white_label_enabled?: boolean;
}

export interface OrganizationPublicPreferencesResponse extends OrganizationPreferencesResponse {
  organization_id: number;
  slug: string;
  school_name: string;
}

export interface OrganizationCommercialSummaryResponse {
  billing_status: "trial" | "active" | "past_due" | "suspended" | "cancelled";
  max_teacher_members: number;
  max_active_students: number;
  max_school_books: number;
}

export type OrganizationRole = string;

export interface OrganizationAccessCapabilities {
  can_manage_roles: boolean;
  can_manage_member_access: boolean;
}
export type OrganizationMembershipStatus = "active" | "suspended";
export type OrganizationInvitationStatus =
  | "invited"
  | "accepted"
  | "cancelled"
  | "expired";
export type OrganizationPermissionKey =
  | "organization.dashboard.access"
  | "organization.reports.view"
  | "organization.settings.view"
  | "organization.settings.manage"
  | "organization.members.view"
  | "organization.members.manage"
  | "organization.billing.view"
  | "organization.billing.manage"
  | "organization.preferences.view"
  | "organization.preferences.manage"
  | "organization.notifications.view"
  | "organization.communications.inbox.view"
  | "organization.communications.manage"
  | "organization.communications.send"
  | "organization.communications.configure"
  | "store.catalog.view"
  | "store.catalog.manage"
  | "store.orders.view"
  | "store.orders.manage"
  | "store.settings.manage"
  | "store.payments.manage"
  | "documents.manage"
  | "students.view"
  | "students.manage"
  | "students.archive"
  | "interests.view"
  | "interests.manage"
  | "forms.view"
  | "forms.manage"
  | "classrooms.view"
  | "classrooms.manage"
  | "classrooms.schedule.manage"
  | "attendance.view"
  | "attendance.manage"
  | "attendance.configure"
  | "scheduling.view"
  | "scheduling.manage"
  | "scheduling.configure"
  | "finance.view"
  | "finance.manage"
  | "content.school_materials.view"
  | "content.school_materials.manage"
  | "content.platform_materials.use"
  | "assessments.view"
  | "assessments.assign"
  | "assessments.manage_custom"
  | "assessments.delete_custom";

export interface OrganizationSummaryResponse {
  id: number;
  slug: string;
  school_name: string;
  role: OrganizationRole;
  role_name?: string;
  capabilities?: OrganizationAccessCapabilities;
  membership_id: number;
  membership_status: OrganizationMembershipStatus;
  teaching_enabled: boolean;
  scheduling_enabled?: boolean;
  permissions: Partial<Record<OrganizationPermissionKey, boolean>>;
  preferences: OrganizationPreferencesResponse | null;
  commercial_config: OrganizationCommercialSummaryResponse | null;
  can_access: boolean;
  access_blocked_reason: string;
}

export interface OrganizationContextResponse {
  active_organization: OrganizationSummaryResponse | null;
  available_organizations: OrganizationSummaryResponse[];
  requires_organization_selection: boolean;
  individual_context_allowed: boolean;
}

export interface SwitchOrganizationContextInput {
  organization_id: number | null;
}

export type SwitchOrganizationContextResponse = LoginResponse &
  OrganizationContextResponse;

export interface OrganizationMemberUserResponse {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
}

export interface OrganizationMemberCapabilities {
  can_update: boolean;
  can_change_role: boolean;
  can_change_permissions: boolean;
}

export interface OrganizationInvitationCapabilities {
  can_resend: boolean;
  can_cancel: boolean;
}

export interface OrganizationMemberResponse {
  id: number;
  organization_id: number;
  user: OrganizationMemberUserResponse;
  capabilities?: OrganizationMemberCapabilities;
  role: OrganizationRole;
  role_name?: string;
  status: OrganizationMembershipStatus;
  teaching_enabled: boolean;
  teaching_disabled_reason: string;
  permission_overrides: Partial<Record<OrganizationPermissionKey, boolean>>;
  permissions: Partial<Record<OrganizationPermissionKey, boolean>>;
  created_at: string;
  updated_at: string;
}

export interface OrganizationMemberUpdateInput {
  role?: OrganizationRole;
  status?: OrganizationMembershipStatus;
  permission_overrides?: Partial<Record<OrganizationPermissionKey, boolean>>;
}

export interface OrganizationInvitationResponse {
  id: number;
  capabilities?: OrganizationInvitationCapabilities;
  email: string;
  role: OrganizationRole;
  role_name?: string;
  teaching_enabled: boolean;
  permission_overrides: Partial<Record<OrganizationPermissionKey, boolean>>;
  status: OrganizationInvitationStatus;
  invited_by: OrganizationMemberUserResponse | null;
  accepted_by: OrganizationMemberUserResponse | null;
  expires_at: string;
  accepted_at: string | null;
  cancelled_at: string | null;
  is_expired: boolean;
  acceptance_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface OrganizationInvitationCreateInput {
  email: string;
  role: OrganizationRole;
  teaching_enabled: boolean;
  permission_overrides?: Partial<Record<OrganizationPermissionKey, boolean>>;
}

export interface OrganizationRolePolicyResponse {
  id: number;
  role: OrganizationRole;
  name: string;
  is_system: boolean;
  is_protected: boolean;
  member_count: number;
  invitation_count: number;
  permissions: Partial<Record<OrganizationPermissionKey, boolean>>;
  created_at: string;
  updated_at: string;
}

export interface OrganizationRolePolicyUpdateInput {
  name?: string;
  permissions: Partial<Record<OrganizationPermissionKey, boolean>>;
}

export interface OrganizationAssignableTeacherResponse {
  membership_id: number;
  teacher_id: number;
  user_id: number;
  name: string;
  email: string;
  role: OrganizationRole;
  teaching_enabled: boolean;
}

export interface OrganizationBillingAccountResponse {
  id: number;
  billing_name: string;
  document_type: "cpf" | "cnpj" | "";
  document_number: string;
  billing_email: string;
  status: string;
  user_access_config: number | null;
  plan_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface OrganizationBillingAccountUpdateInput {
  billing_name?: string;
  document_type?: "cpf" | "cnpj" | "";
  document_number?: string;
  billing_email?: string;
}

export interface OrganizationCommercialConfigResponse {
  id: number;
  billing_status: OrganizationCommercialSummaryResponse["billing_status"];
  max_teacher_members: number;
  max_active_students: number;
  max_school_books: number;
  created_at: string;
  updated_at: string;
}

export interface OrganizationCommercialUsageMetricResponse {
  used: number;
  pending: number;
  blocking_used: number;
  limit: number | null;
  remaining: number | null;
  exceeded: boolean;
  blocks_growth: boolean;
}

export interface OrganizationCommercialUsageResponse {
  organization_id: number;
  billing_status: OrganizationCommercialSummaryResponse["billing_status"];
  billing_account_status: string;
  plan_name: string | null;
  usage: {
    teacher_members: OrganizationCommercialUsageMetricResponse;
    active_students: OrganizationCommercialUsageMetricResponse;
    school_books: OrganizationCommercialUsageMetricResponse;
  };
}

export interface OrganizationReportsOverviewResponse {
  organization_id: number;
  members: {
    total: number;
    active: number;
    teaching_enabled: number;
  };
  students: {
    total: number;
    active: number;
    archived: number;
  };
  classrooms: {
    total: number;
    active: number;
    deleted: number;
  };
  school_books: {
    total: number;
  };
  usage: OrganizationCommercialUsageResponse["usage"];
}

export const ORGANIZATION_ROLE_LABELS: Record<OrganizationRole, string> = {
  owner: "Administrador",
  manager: "Gestor",
  teacher: "Professor",
};

export const ORGANIZATION_MEMBERSHIP_STATUS_LABELS: Record<
  OrganizationMembershipStatus,
  string
> = {
  active: "Ativo",
  suspended: "Suspenso",
};

export const ORGANIZATION_INVITATION_STATUS_LABELS: Record<
  OrganizationInvitationStatus,
  string
> = {
  invited: "Pendente",
  accepted: "Aceito",
  cancelled: "Cancelado",
  expired: "Expirado",
};

export const ORGANIZATION_PERMISSION_GROUPS: {
  title: string;
  items: {
    key: OrganizationPermissionKey;
    label: string;
    description?: string;
  }[];
}[] = [
  {
    title: "Formulários",
    items: [
      { key: "forms.view", label: "Ver formulários, respostas e exportar" },
      { key: "forms.manage", label: "Criar e gerenciar formulários" },
    ],
  },
  {
    title: "Interessados",
    items: [
      { key: "interests.view", label: "Ver interessados da escola" },
      {
        key: "interests.manage",
        label: "Gerenciar interessados e registrar contatos",
      },
    ],
  },
  {
    title: "Loja",
    items: [
      { key: "store.catalog.view", label: "Ver catálogo da loja" },
      { key: "store.catalog.manage", label: "Gerenciar produtos e categorias" },
      { key: "store.orders.view", label: "Ver pedidos da loja" },
      { key: "store.orders.manage", label: "Gerenciar pedidos e entregas" },
      { key: "store.settings.manage", label: "Configurar e publicar a loja" },
      {
        key: "store.payments.manage",
        label: "Gerenciar recebimentos e conciliação",
      },
    ],
  },
  {
    title: "Administração",
    items: [
      {
        key: "organization.dashboard.access",
        label: "Acessar gestão da escola",
      },
      { key: "organization.settings.view", label: "Ver configurações" },
      { key: "organization.settings.manage", label: "Editar configurações" },
      { key: "organization.members.view", label: "Ver equipe" },
      { key: "organization.members.manage", label: "Gerenciar equipe" },
      { key: "organization.billing.view", label: "Ver dados comerciais" },
      {
        key: "organization.billing.manage",
        label: "Gerenciar dados comerciais",
      },
      { key: "organization.preferences.view", label: "Ver preferências" },
      { key: "organization.preferences.manage", label: "Editar preferências" },
      {
        key: "organization.notifications.view",
        label: "Ver comunicações da escola",
        description:
          "Permite consultar mensagens enviadas pela escola e seus estados de entrega.",
      },
      {
        key: "organization.communications.inbox.view",
        label: "Ver conversas de email e WhatsApp",
        description:
          "Consultar as mensagens recebidas e enviadas na central compartilhada da escola.",
      },
      {
        key: "organization.communications.manage",
        label: "Gerenciar comunicados e emails",
        description:
          "Criar comunicados na plataforma e preparar emails para os alunos.",
      },
      {
        key: "organization.communications.send",
        label: "Enviar mensagens e agendar emails",
      },
      {
        key: "organization.communications.configure",
        label: "Configurar canais de comunicação",
        description:
          "Conectar o email e o WhatsApp da escola e testar o envio de emails.",
      },
    ],
  },
  {
    title: "Documentos",
    items: [
      {
        key: "documents.manage",
        label: "Gerenciar documentos da escola",
        description:
          "Permite criar modelos, emitir documentos e editar os dados da escola usados nos documentos.",
      },
    ],
  },
  {
    title: "Alunos e turmas",
    items: [
      { key: "organization.reports.view", label: "Ver relatórios acadêmicos" },
      { key: "students.view", label: "Ver alunos" },
      { key: "students.manage", label: "Gerenciar alunos" },
      { key: "students.archive", label: "Arquivar alunos" },
      { key: "classrooms.view", label: "Ver turmas" },
      { key: "classrooms.manage", label: "Editar turmas atribuídas" },
      { key: "classrooms.schedule.manage", label: "Gerenciar agenda" },
      { key: "attendance.view", label: "Ver presença" },
      { key: "attendance.manage", label: "Registrar presença e reposições" },
      {
        key: "attendance.configure",
        label: "Configurar alertas de presença",
      },
      { key: "scheduling.view", label: "Ver agendamentos" },
      { key: "scheduling.manage", label: "Gerenciar reuniões" },
      { key: "scheduling.configure", label: "Configurar agendamentos" },
    ],
  },
  {
    title: "Financeiro",
    items: [
      { key: "finance.view", label: "Ver financeiro dos alunos" },
      {
        key: "finance.manage",
        label: "Gerenciar mensalidades e pagamentos",
        description:
          "Configurar mensalidades, lançar valores e registrar, reabrir ou cancelar pagamentos da escola.",
      },
    ],
  },
  {
    title: "Materiais e avaliações",
    items: [
      {
        key: "content.school_materials.view",
        label: "Ver materiais da escola",
      },
      {
        key: "content.school_materials.manage",
        label: "Gerenciar materiais da escola",
      },
      {
        key: "content.platform_materials.use",
        label: "Usar materiais TeachPlus",
      },
      { key: "assessments.view", label: "Ver avaliações" },
      { key: "assessments.assign", label: "Atribuir avaliações" },
      {
        key: "assessments.manage_custom",
        label: "Gerenciar avaliações próprias",
      },
      {
        key: "assessments.delete_custom",
        label: "Excluir quizzes permanentemente",
        description:
          "Permite excluir quizzes próprios e apagar aplicações, tentativas e resultados dos alunos.",
      },
    ],
  },
];

export type OrganizationDomainStatus =
  | "requested"
  | "in_progress"
  | "awaiting_dns"
  | "active"
  | "rejected"
  | "cancelled";
export interface OrganizationDnsRecord {
  type: string;
  name: string;
  value: string;
}
export interface OrganizationDomainRequest {
  id: number;
  purpose?: "product" | "store";
  domain: string;
  status: OrganizationDomainStatus;
  version: number;
  domain_verified: boolean;
  domain_verified_at: string | null;
  domain_verification_token: string;
  dns_verification: OrganizationDnsRecord;
  dns_instructions: OrganizationDnsRecord[];
  customer_message: string;
  created_at: string;
  updated_at: string;
  activated_at: string | null;
  legacy_review_pending: boolean;
  internal_notes?: string;
  hosting_confirmed?: boolean;
  https_confirmed?: boolean;
  recaptcha_confirmed?: boolean;
}
export interface OrganizationDomainRequestsResponse {
  active_domain: OrganizationDomainRequest | null;
  pending_request: OrganizationDomainRequest | null;
  requests: OrganizationDomainRequest[];
}
export interface OrganizationDomainUpdateInput {
  expected_version: number;
  status?: "requested" | "in_progress" | "awaiting_dns" | "rejected";
  dns_instructions?: OrganizationDnsRecord[];
  customer_message?: string;
  internal_notes?: string;
}
export interface OrganizationDomainActivationInput {
  expected_version: number;
  expected_domain: string;
  expected_verification_token: string;
  hosting_confirmed: boolean;
  https_confirmed: boolean;
  recaptcha_confirmed: boolean;
}
